import { useEffect, useState } from "react";
import { CalendarClock, Sparkles, Split, Rows3, Timer, AlertTriangle, Unlock, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { fmtD } from "./gUtils";

const hhmm = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
const fmtDur = (m) => `${Math.floor(m / 60)}h${String(m % 60).padStart(2, "0")}`;
const MODES = [["auto", "Automatique", Sparkles], ["continu", "Continu", Rows3], ["fractionne", "Fractionné", Split]];

function SlotLine({ s }) {
  return (
    <p className="text-xs text-neutral-600">
      {fmtD(s.date)} — <strong>{hhmm(s.start_min)} → {hhmm(s.end_min)}</strong>
      <span className="text-neutral-400"> ({fmtDur(s.end_min - s.start_min)})</span>
    </p>
  );
}

export default function PlanningTab({ project: p, onChanged, act, busy }) {
  const [mode, setMode] = useState("auto");
  const [options, setOptions] = useState(null);
  const [planning, setPlanning] = useState(null);
  const [holdHours, setHoldHours] = useState(48);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settings, setSettings] = useState(null);

  const loadPlanning = () => api.get(`/graphisme-planning/projects/${p.id}/planning`)
    .then((r) => { setPlanning(r.data); setHoldHours(r.data.settings?.hold_hours || 48); })
    .catch(() => {});
  const loadOptions = (m) => {
    setOptions(null);
    api.get(`/graphisme-planning/projects/${p.id}/slot-options`, { params: { mode: m } })
      .then((r) => setOptions(r.data))
      .catch(() => setOptions({ options: [], alert: "Erreur de recherche" }));
  };
  useEffect(() => { loadPlanning(); }, [p.id, p.planned_minutes, p.status]); // eslint-disable-line
  useEffect(() => { loadOptions(mode); }, [mode, p.id, p.planned_minutes]); // eslint-disable-line

  const reserve = async (opt) => {
    await act(async () => {
      const r = await api.post(`/graphisme-planning/projects/${p.id}/reserve`,
        { slots: opt.slots, mode: opt.mode, hold_hours: holdHours });
      if (r.data.booking_status === "provisoire") {
        toast.success(`Créneaux réservés provisoirement (expiration dans ${holdHours} h)`);
      } else {
        toast.success("Créneaux confirmés dans l'agenda — client informé");
      }
      loadPlanning();
      onChanged();
    }, null);
  };
  const release = async () => {
    if (!window.confirm("Libérer les réservations provisoires de ce projet ?")) return;
    await act(async () => {
      await api.post(`/graphisme-planning/projects/${p.id}/release`);
      loadPlanning(); onChanged();
    }, "Réservations libérées");
  };
  const saveSettings = async () => {
    try {
      await api.put("/graphisme-planning/settings", {
        min_session_min: +settings.min_session_min || 30,
        hold_hours: +settings.hold_hours || 48,
        internal_hourly_rate: +settings.internal_hourly_rate || 0,
        client_visibility: settings.client_visibility,
      });
      toast.success("Paramètres de planification enregistrés");
      setSettingsOpen(false); loadPlanning(); loadOptions(mode);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const provisoire = planning?.booking_status === "provisoire";
  const confirme = planning?.booking_status === "confirme";
  return (
    <div className="space-y-4" data-testid="planning-tab">
      {/* Temps : estimé / réservé / réel / restant */}
      {planning && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5" data-testid="planning-time-cards">
          {[["Temps estimé", planning.estimated_minutes, "text-neutral-700"],
            ["Réservé agenda", planning.reserved_minutes, "text-blue-700"],
            ["Réellement effectué", planning.real_minutes, "text-emerald-700"],
            ["Restant à produire", planning.remaining_minutes, planning.remaining_minutes > 0 ? "text-orange-600" : "text-emerald-700"]].map(([l, v, cls], i) => (
            <div key={l} className="bg-neutral-50 border border-neutral-200 rounded-xl p-3" data-testid={`planning-time-${i}`}>
              <p className="text-[10px] uppercase tracking-wider text-neutral-400">{l}</p>
              <p className={`font-heading font-bold text-lg mt-0.5 ${cls}`}>{v ? fmtDur(v) : "—"}</p>
            </div>
          ))}
        </div>
      )}
      {planning?.overtime_minutes > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-xs text-red-700 flex items-center gap-2" data-testid="planning-overtime">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Dépassement du temps estimé : +{fmtDur(planning.overtime_minutes)} effectuées au-delà de l'estimation.
        </div>
      )}
      {planning?.needs_replan && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-800 flex items-center gap-2" data-testid="planning-replan-alert">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          Ce projet nécessite du temps supplémentaire — sélectionnez une option ci-dessous pour réserver un nouveau créneau.
        </div>
      )}

      {/* Réservations en cours */}
      {planning && planning.slots.length > 0 && (
        <div className={`rounded-xl border p-4 ${provisoire ? "bg-amber-50/60 border-amber-200 border-dashed" : "bg-white border-neutral-200"}`} data-testid="planning-reservations">
          <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
            <h4 className="font-heading font-bold text-sm flex items-center gap-2">
              <CalendarClock className="w-4 h-4" />
              {provisoire ? "Réservation provisoire" : "Créneaux confirmés"}
            </h4>
            <div className="flex items-center gap-2">
              {provisoire && planning.hold_expires_at && (
                <span className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5" data-testid="planning-expiry">
                  Expire le {new Date(planning.hold_expires_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                </span>
              )}
              {provisoire && (
                <Button variant="outline" size="sm" className="h-7 text-xs gap-1" onClick={release} disabled={busy} data-testid="planning-release">
                  <Unlock className="w-3 h-3" /> Libérer
                </Button>
              )}
            </div>
          </div>
          <div className="space-y-1">
            {planning.slots.map((s) => (
              <div key={s.block_id} className="flex items-center justify-between" data-testid={`planning-slot-${s.block_id}`}>
                <SlotLine s={s} />
                {provisoire && <span className="text-[10px] text-amber-600 font-medium">provisoire</span>}
              </div>
            ))}
          </div>
          {provisoire && (
            <p className="text-[11px] text-amber-700 mt-2">Bloqués dans l'agenda en attente de validation du devis — ils seront confirmés automatiquement à l'acceptation, ou libérés à l'expiration.</p>
          )}
        </div>
      )}

      {/* Recherche de créneaux */}
      <div className="bg-white border border-neutral-200 rounded-xl p-4" data-testid="planning-search">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
          <h4 className="font-heading font-bold text-sm flex items-center gap-2"><Sparkles className="w-4 h-4 text-[#1D4ED8]" /> Trouver des créneaux dans mon agenda</h4>
          <button onClick={() => { setSettingsOpen(!settingsOpen); if (!settings && planning) setSettings({ ...planning.settings }); }}
            className="text-xs text-neutral-400 hover:text-neutral-700 flex items-center gap-1" data-testid="planning-settings-toggle">
            <Settings2 className="w-3.5 h-3.5" /> Paramètres
          </button>
        </div>
        {settingsOpen && settings && (
          <div className="bg-neutral-50 border border-neutral-200 rounded-lg p-3 mb-3 grid sm:grid-cols-2 gap-2 text-xs" data-testid="planning-settings">
            <label>Durée min. par session (min)
              <input type="number" min="15" step="15" className="mt-1 w-full border border-neutral-200 rounded-md px-2 py-1.5" data-testid="planning-set-min-session"
                value={settings.min_session_min} onChange={(e) => setSettings({ ...settings, min_session_min: e.target.value })} /></label>
            <label>Durée de réservation provisoire (h)
              <select className="mt-1 w-full border border-neutral-200 rounded-md px-2 py-1.5 bg-white" data-testid="planning-set-hold"
                value={settings.hold_hours} onChange={(e) => setSettings({ ...settings, hold_hours: e.target.value })}>
                <option value="24">24 h</option><option value="48">48 h (défaut)</option><option value="72">72 h</option><option value="168">1 semaine</option>
              </select></label>
            <label>Taux horaire interne (€/h)
              <input type="number" min="0" className="mt-1 w-full border border-neutral-200 rounded-md px-2 py-1.5" data-testid="planning-set-rate"
                value={settings.internal_hourly_rate} onChange={(e) => setSettings({ ...settings, internal_hourly_rate: e.target.value })} /></label>
            <label>Visibilité client du planning
              <select className="mt-1 w-full border border-neutral-200 rounded-md px-2 py-1.5 bg-white" data-testid="planning-set-visibility"
                value={settings.client_visibility} onChange={(e) => setSettings({ ...settings, client_visibility: e.target.value })}>
                <option value="delai">Délai prévisionnel uniquement</option>
                <option value="dates">Dates de réalisation</option>
                <option value="creneaux">Créneaux exacts</option>
              </select></label>
            <div className="sm:col-span-2"><Button size="sm" className="bg-neutral-900 text-white h-7 text-xs" onClick={saveSettings} data-testid="planning-settings-save">Enregistrer</Button></div>
          </div>
        )}
        <div className="flex items-center gap-1.5 mb-3 flex-wrap">
          {MODES.map(([id, label, Icon]) => (
            <button key={id} onClick={() => setMode(id)} data-testid={`planning-mode-${id}`}
              className={`flex items-center gap-1.5 text-xs font-medium rounded-full px-3 py-1.5 border transition-colors ${mode === id ? "bg-[#0B132B] text-white border-[#0B132B]" : "bg-white text-neutral-500 border-neutral-200 hover:border-neutral-400"}`}>
              <Icon className="w-3.5 h-3.5" /> {label}
            </button>
          ))}
          {provisoire && (
            <label className="ml-auto text-xs text-neutral-500 flex items-center gap-1.5">Expiration
              <select value={holdHours} onChange={(e) => setHoldHours(+e.target.value)} className="border border-neutral-200 rounded-md px-1.5 py-1 bg-white" data-testid="planning-hold-hours">
                <option value={24}>24 h</option><option value={48}>48 h</option><option value={72}>72 h</option>
              </select></label>
          )}
        </div>
        {options === null ? (
          <p className="text-xs text-neutral-400 animate-pulse flex items-center gap-2"><Timer className="w-3.5 h-3.5" /> Analyse de l'agenda…</p>
        ) : (
          <>
            {options.alert && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg p-2.5 mb-2 flex items-center gap-2" data-testid="planning-alert">
                <AlertTriangle className="w-4 h-4 shrink-0" /> {options.alert}
              </p>
            )}
            {options.options?.length === 0 && !options.alert && (
              <p className="text-xs text-neutral-400" data-testid="planning-empty">Aucune disponibilité trouvée — essayez le mode fractionné ou choisissez manuellement ci-dessous.</p>
            )}
            {options.options?.length === 1 && (
              <p className="text-[11px] text-neutral-400 mb-2" data-testid="planning-single-option">Une seule option trouvée dans les disponibilités — élargissez la deadline pour plus de choix.</p>
            )}
            <div className="space-y-2">
              {(options.options || []).map((opt) => (
                <div key={opt.id} className="border border-neutral-200 rounded-lg p-3 hover:border-[#1D4ED8]/40 transition-colors" data-testid={`planning-option-${opt.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-xs font-semibold text-neutral-700">
                      Option {opt.id} — {opt.label} · {fmtDur(opt.total_minutes)}
                    </p>
                    <Button size="sm" className="h-7 text-xs bg-[#1D4ED8] hover:bg-[#1E40AF] text-white" disabled={busy}
                      onClick={() => reserve(opt)} data-testid={`planning-reserve-${opt.id}`}>
                      {confirme || ["a_planifier", "planifie", "planifie_partiel", "en_cours"].includes(p.status) ? "Réserver (confirmé)" : "Réserver (provisoire)"}
                    </Button>
                  </div>
                  <div className="mt-1.5 space-y-0.5">{opt.slots.map((s, i) => <SlotLine key={i} s={s} />)}</div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
